import type { FilmProject } from "../types";
import type { MissingInput } from "../utils/analysisSummary";
import { agentPlanSummary, formatPlanRuntime, goalLabel } from "../utils/analysisSummary";
import { formatRuntime } from "../utils/storyboard";
import { Icon } from "./Icon";

interface AIPlanReviewProps {
  analysis: Record<string, unknown>;
  project: FilmProject;
  onApprove: () => void;
  onBack: () => void;
  onResolveMissingInput: (item: MissingInput) => void;
}

export function AIPlanReview({ analysis, project, onApprove, onBack, onResolveMissingInput }: AIPlanReviewProps) {
  const summary = agentPlanSummary(analysis, project);
  const nextActions = summary.nextActions.length
    ? summary.nextActions
    : [
        { label: "Approve plan and generate boards", owner: "user", status: "ready" as const },
        { label: "Upload any missing source inputs", owner: "user", status: "needs_user" as const },
        { label: "Adjust beats or switch goal in setup", owner: "user", status: "ready" as const }
      ];
  const approvalGates = summary.approvalGates.length
    ? summary.approvalGates
    : [
        "Approve the parsed plan before any storyboard generation.",
        "Uploaded and locked assets remain source of truth.",
        "Live EvoLink send stays disabled until rights-safe confirmation."
      ];

  return (
    <div className="flow-view ai-plan-view">
      <header className="flow-hero compact">
        <div>
          <span>{project.title}</span>
          <h1>Production Agent plan</h1>
          <p>{goalLabel(summary.goal)} - {formatRuntime(summary.runtimeSeconds)} - {project.aspectRatio} - {project.resolution} test</p>
        </div>
        <div className="flow-actions">
          <button className="secondary-action" onClick={onBack} type="button">
            Adjust beats
          </button>
          <button className="primary-action" onClick={onApprove} type="button">
            <Icon name="spark" />
            Approve & Generate Boards
          </button>
        </div>
      </header>

      <section className="ai-plan-grid">
        <article className="agent-card">
          <header>
            <div>
              <span className="agent-dot" />
              <h2>{summary.agentName}</h2>
            </div>
            <div className="agent-badges">
              <span>Goal - {goalLabel(summary.goal)}</span>
              <span>Confidence {summary.confidence}%</span>
            </div>
          </header>

          <p><strong>Objective:</strong> {summary.objective}</p>
          <p><strong>Strategy:</strong> {summary.strategy}</p>

          <div className="beat-plan">
            <span>Beat Plan</span>
            <div className="beat-list">
              {summary.beats.map((beat) => (
                <div className="beat-row" key={beat.id}>
                  <span className="beat-label">{beat.label}</span>
                  <div>
                    <strong>{beat.sceneLabel}</strong>
                    <small>{beat.note}</small>
                  </div>
                  <time>{beat.seconds}s</time>
                  <em className={beat.readiness === "ready" ? "ready" : "needs-input"}>
                    {beat.readiness === "ready" ? "Ready" : "Needs input"}
                  </em>
                </div>
              ))}
            </div>
          </div>

          <footer>
            <p>{summary.why}</p>
          </footer>
        </article>

        <aside className="plan-side-stack">
          <article className="setup-card chunks-card">
            <div className="card-heading">
              <span>Runtime & Chunks</span>
              <h2>{formatPlanRuntime(summary)}</h2>
            </div>
            <div className="chunk-bar" aria-label="Chunk timing">
              {Array.from({ length: Math.max(1, Math.min(summary.chunkCount, 12)) }).map((_, index) => (
                <span key={index} />
              ))}
            </div>
            <p>Chunks are auto-derived from beats. Edit, split, merge, reorder, disable, or lock them later in Runtime & Chunking.</p>
          </article>

          <article className="setup-card missing-card">
            <div className="card-heading">
              <span>Missing Inputs - {summary.missingInputs.length}</span>
              <h2>Upload before final export</h2>
            </div>
            {summary.missingInputs.length ? (
              <div className="missing-input-list compact">
                {summary.missingInputs.map((item) => (
                  <div key={item.id}>
                    <span className={item.required ? "required-dot" : "optional-dot"} />
                    <div>
                      <strong>{item.label}</strong>
                      <small>{item.kind}</small>
                    </div>
                    <button className="secondary-action" onClick={() => onResolveMissingInput(item)} type="button">Resolve</button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-copy">No required inputs are missing for this plan.</p>
            )}
          </article>

          <article className="setup-card next-actions-card">
            <div className="card-heading">
              <span>Next Actions</span>
              <h2>What happens next</h2>
            </div>
            <ol className="next-action-list">
              {nextActions.map((action, index) => (
                <li className={`next-action ${action.status}`} key={`${action.label}-${index}`}>
                  <span>{action.label}</span>
                  <em>{action.status === "needs_user" ? "Needs user" : action.status === "blocked" ? "Blocked" : "Ready"} · {action.owner}</em>
                </li>
              ))}
            </ol>
          </article>

          <article className="setup-card approval-gates-card">
            <div className="card-heading">
              <span>Approval Gates</span>
              <h2>Before Generate Boards</h2>
            </div>
            <div className="approval-gate-list">
              {approvalGates.map((gate, index) => (
                <div key={`${gate}-${index}`}>
                  <span>{index + 1}</span>
                  <p>{gate}</p>
                </div>
              ))}
            </div>
          </article>

          <article className="setup-card agent-limits-card">
            <div className="card-heading">
              <span>Agent Limits</span>
              <h2>Director approval stays in control</h2>
            </div>
            <p>The agent proposes plans and assembles packages only. It cannot spend provider credits, overwrite uploaded or locked assets, approve rights, or claim EvoLink runs completed.</p>
          </article>
        </aside>
      </section>
    </div>
  );
}
